import type { ReleaseInfo } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { Badge } from '@sunstone/ui';
import { ExternalLink, Flame, GitBranch } from 'lucide-react';

export function imageTag(image: string): string {
  const idx = image.lastIndexOf(':');
  return idx === -1 ? image : image.slice(idx + 1);
}

export function shortSha(sha: string): string {
  return sha ? sha.slice(0, 7) : '';
}

export function RepoLink({ gitRepo }: { gitRepo: string }) {
  if (!gitRepo) return null;
  if (!gitRepo.startsWith('http')) {
    return (
      <span
        title={gitRepo}
        className="truncate max-w-40 shrink text-xs font-mono text-muted-foreground"
      >
        {gitRepo}
      </span>
    );
  }
  return (
    <a
      href={gitRepo}
      target="_blank"
      rel="noopener noreferrer"
      title={gitRepo}
      className="shrink-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
    >
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

/**
 * Top-of-card band showing a release's own metadata (version, hotfix flag,
 * branch, start date, comment) - shared between ReleaseDiffDialog (a
 * refreshed manifest being previewed) and DeployedComponentsPanel (the
 * manifest currently deployed to a workspace).
 */
export function ReleaseHeaderBand({ release }: { release?: ReleaseInfo }) {
  if (!release?.version) return null;
  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-semibold text-foreground">{release.version}</span>
          {release.isHotfix && (
            <Badge
              variant="outline"
              className="gap-1 border-amber-600/40 text-xs text-amber-600 dark:border-amber-500/40 dark:text-amber-500"
            >
              <Flame className="h-3 w-3" />
              Hotfix
            </Badge>
          )}
          {release.branch && (
            <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
              <GitBranch className="h-3 w-3" />
              {release.branch}
            </span>
          )}
        </div>
        {release.startDate && (
          <span className="shrink-0 text-xs text-muted-foreground">
            Started {release.startDate}
          </span>
        )}
      </div>
      {release.comment && (
        <p className="text-sm italic text-muted-foreground">&ldquo;{release.comment}&rdquo;</p>
      )}
    </div>
  );
}
